import { t } from "@repo/translation";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import { type ReactElement } from "react";

export interface SalesLeadError {
  api: string;
  applicationName: string;
  applicationType: string;
  code: string;
  debugInfo: string;
  group: string;
  message: string;
  status: string;
  type: string;
}

export interface SlaStarterGenericResponse {
  result: string;
}

export const isSalesLeadError = (error: unknown): error is SalesLeadError =>
  error ? Object.prototype.hasOwnProperty.call(error, "debugInfo") : false;

export function getErrorMessage(error: unknown) {
  if (error instanceof Error) {
    if (error.message) {
      return error.message;
    } else if (error.cause && Object.hasOwn(error.cause, "result")) {
      return (error.cause as SlaStarterGenericResponse).result;
    }
  }
  return error ? String(error) : "Unknown error";
}

export function getDebugMessage(error: unknown) {
  if (error instanceof Error) {
    if (isSalesLeadError(error.cause)) {
      return error.cause.debugInfo;
    }
    return getErrorMessage(error);
  }
  return String(error);
}

interface ApiErrorCause {
  detail?: string;
  title?: string;
  status?: number;
  debugInfo?: string;
  message?: string;
}

export function getAppointmentApiErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const cause = error.cause as ApiErrorCause | undefined;

    if (cause?.detail) {
      const translated = t(`prompt.${cause.detail}`);
      if (translated !== `prompt.${cause.detail}`) {
        return translated;
      }
      return cause.detail;
    }

    if (cause?.title) {
      return cause.title;
    }

    if (cause?.message) {
      return cause.message;
    }
  }

  return getErrorMessage(error);
}

/**
 * Checks if an error is related to file size limit (HTTP 413 Payload Too Large)
 * Handles CloudRun HTML responses and our custom error messages
 */
export function isFileSizeError(error: unknown): boolean {
  // Check for statusCode 413
  const statusCode = (error as any)?.statusCode;
  if (statusCode === 413) {
    return true;
  }

  const errorMessage = getErrorMessage(error).toLowerCase();
  return errorMessage.includes("request entity too large");
}

export interface FileUploadErrorInfo {
  title: string;
  message?: string;
}

/**
 * Returns localized title and message for file upload errors
 * Handles file size errors specially with a user-friendly message
 * @param error The error object
 * @param options Configuration for fallback messages
 * @param options.defaultTitleKey Translation key for non-size errors (default: "prompt.file_uploaded_failed")
 * @param options.defaultMessageKey Translation key for message, if not provided uses getErrorMessage(error)
 */
export function getFileUploadErrorInfo(
  error: unknown,
  options?: {
    defaultTitleKey?: string;
    defaultMessageKey?: string;
  }
): FileUploadErrorInfo {
  const { defaultTitleKey = "prompt.file_uploaded_failed", defaultMessageKey } =
    options || {};

  if (isFileSizeError(error)) {
    return {
      title: t("prompt.file_size_limit_error"),
      message: t("prompt.file_size_limit_error_message"),
    };
  }

  return {
    title: t(defaultTitleKey),
    message: defaultMessageKey ? t(defaultMessageKey) : getErrorMessage(error),
  };
}

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export function isDefined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export type Values<T> = T[keyof T];

export function hasItems<T>(value?: T[]): value is T[] {
  return isDefined(value) && value.length > 0;
}

export function isLastItem<T>(value: T[] | undefined, index: number) {
  if (!value) {
    return false;
  }
  return index === value.length - 1;
}

export const isElement = (
  value?: string | number | ReactElement
): value is ReactElement =>
  !isString(value) && !isNumber(value) && value !== undefined;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function assertUnreachable(x: never) {
  throw new Error("Didn't expect to get here");
}

export type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;
